
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertCircle, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

interface PenaltiesSettingsProps {
  providerId: string;
}

const PenaltiesSettings = ({ providerId }: PenaltiesSettingsProps) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ['penalties-settings', providerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('penalties_settings')
        .select('*')
        .eq('provider_id', providerId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data || {
        late_checkout_enabled: false,
        max_overstay_minutes: 5,
        late_checkout_fee: 10.00,
        currency: 'GBP'
      };
    },
    enabled: !!providerId,
  });

  const [formData, setFormData] = useState({
    late_checkout_enabled: false,
    max_overstay_minutes: 5,
    late_checkout_fee: 10.00,
    currency: 'GBP'
  });

  React.useEffect(() => {
    if (settings) {
      setFormData({
        late_checkout_enabled: settings.late_checkout_enabled,
        max_overstay_minutes: settings.max_overstay_minutes,
        late_checkout_fee: Number(settings.late_checkout_fee),
        currency: settings.currency || 'GBP'
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('penalties_settings')
        .upsert({
          provider_id: providerId,
          ...formData,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'provider_id'
        });

      if (error) throw error;

      toast({
        title: "Settings Saved",
        description: "Penalties settings have been updated successfully.",
      });

      refetch();
    } catch (error) {
      console.error('Error saving penalties settings:', error);
      toast({
        title: "Error",
        description: "Failed to save penalties settings.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5" />
            <span>Late Checkout Penalties</span>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="late-checkout-enabled">Enable Late Checkout Fees</Label>
              <p className="text-sm text-gray-600">
                Automatically charge customers who overstay their booking time
              </p>
            </div>
            <Switch
              id="late-checkout-enabled"
              checked={formData.late_checkout_enabled}
              onCheckedChange={(checked) => 
                setFormData(prev => ({ ...prev, late_checkout_enabled: checked }))
              }
            />
          </div>

          {formData.late_checkout_enabled && (
            <div className="space-y-4 pt-4 border-t">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="max-overstay">Grace Period (minutes)</Label>
                  <Input
                    id="max-overstay"
                    type="number"
                    min="0"
                    max="60"
                    value={formData.max_overstay_minutes}
                    onChange={(e) => 
                      setFormData(prev => ({ 
                        ...prev, 
                        max_overstay_minutes: parseInt(e.target.value) || 0 
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Customers can overstay this many minutes without penalty
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="late-fee">Late Checkout Fee (£)</Label>
                  <Input
                    id="late-fee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.late_checkout_fee}
                    onChange={(e) => 
                      setFormData(prev => ({ 
                        ...prev, 
                        late_checkout_fee: parseFloat(e.target.value) || 0 
                      }))
                    }
                  />
                  <p className="text-xs text-gray-500">
                    Fixed fee charged for late checkout
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PenaltiesSettings;
